import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, type components } from '@babomrs/api-client';
import { useClientAuth } from '../../hooks/useClientAuth';

type S = components['schemas'];
export type ClientNotificationItem = S['ClientNotificationItem'];
export type ClientNotificationList = S['ClientNotificationListResponse'];

/**
 * Story #26236 (BR-CP-HOME-040..044) — the person's notifications across every entity they can view. Keyed under
 * `['client', 'notifications']` and listed in useActiveEntity's PERSON_SCOPED set, so an entity switch neither drops
 * the bell's count nor re-reads the list: the server never reads the active entity for it (BR-CP-HOME-043).
 */
export const notificationsKey = ['client', 'notifications'] as const;

/** How often the bell re-reads its list while the tab is visible — as the staff bell does. */
export const NOTIFICATIONS_POLL_MS = 60_000;

export function useNotifications() {
  const { user } = useClientAuth();
  return useQuery<ClientNotificationList>({
    queryKey: notificationsKey,
    queryFn: () => apiClient.get<ClientNotificationList>('/api/v1/client/notifications'),
    enabled: !!user,
    retry: false,
    refetchInterval: NOTIFICATIONS_POLL_MS,
    // A hidden tab does not poll; it re-reads when it is shown again.
    refetchIntervalInBackground: false,
  });
}

/**
 * BR-CP-HOME-044 — the client opened an item (Open, or Switch And Open once the switch succeeded). Idempotent on the
 * server; the list is re-read afterwards so the Unread mark and the bell's count fall.
 */
export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiClient.post<void>(`/api/v1/client/notifications/${id}/read`),
    onSettled: () => queryClient.invalidateQueries({ queryKey: notificationsKey }),
  });
}
